import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import JsonLd from "@/components/base/JsonLd";
import SectionHeading from "@/components/base/SectionHeading";

const SITE = "https://esjxdt.cn";

const principles = [
  {
    icon: "ri-lock-2-line",
    title: "来源优先保护",
    desc: "投稿通道不要求任何身份信息，系统层面不记录 IP 与设备指纹。我们宁可放弃一条线索，也不拿来源开玩笑。",
  },
  {
    icon: "ri-node-tree",
    title: "区分事实与判断",
    desc: "正文中的客观描述与编辑推测分开呈现。凡是无法核实的部分，都会在文中明确标出，不作模糊化处理。",
  },
  {
    icon: "ri-refresh-line",
    title: "允许更正与撤销",
    desc: "线索等级会随着新证据上下调整。若关键事实被推翻，我们更正原文并保留更正说明，不做静默删除。",
  },
  {
    icon: "ri-forbid-2-line",
    title: "不做人身攻击",
    desc: "只讨论流程、机制与合作方式，不针对个人进行道德审判。涉及具体纠纷的内容，一律回到可核对的事实层面。",
  },
];

const milestones = [
  { year: "2023", title: "从一封邮件开始", desc: "最初只是几位行业从业者用邮件互相交换观察，后来逐渐形成一个固定的投稿入口。" },
  { year: "2024", title: "建立分级标准", desc: "把口口相传的经验整理成四个可信等级，让读者一眼看清每条线索掌握到什么程度。" },
  { year: "2025", title: "固定的夜班值守", desc: "编辑室把值守时间定在凌晨，因为绝大多数投稿都发生在收工之后。" },
  { year: "2026", title: "线索库对外开放", desc: "把所有完成整理的条目按分类公开，让信息可以被检索、被引用、被检验。" },
];

const voices = [
  {
    quote:
      "写完之后最担心的是被认出。结果编辑回信只问了一句「那段休息时间是两次还是三次」，其他什么都没问。",
    role: "剧组场务 · 匿名线人",
  },
  {
    quote: "同一个现象，他们在三个不同组别里都找到了说法，才敢写出来。这种克制反而让我更愿意继续投。",
    role: "演出执行 · 匿名线人",
  },
  {
    quote: "作为读者，我最看重的是等级标注。哪条是多方印证、哪条只有一个人说，页面上一眼就能看到。",
    role: "内容研究者 · 长期读者",
  },
];

export default function AboutPage() {
  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "AboutPage",
        name: "关于本站 - 深夜爆料",
        url: `${SITE}/about`,
        inLanguage: "zh-CN",
        description: "深夜爆料的品牌介绍、内容原则、核验标准与联系方式。",
        isPartOf: { "@type": "WebSite", name: "深夜爆料", url: `${SITE}/` },
      },
      {
        "@type": "Organization",
        "@id": `${SITE}/#organization`,
        name: "深夜爆料编辑室",
        url: `${SITE}/`,
        email: "tips@midnight-leaks.example.com",
        description: "接收线人、知情者与内部来源匿名投稿，并对线索进行脱敏、交叉印证与分级整理的编辑团队。",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "关于本站", item: `${SITE}/about` },
        ],
      },
    ],
  };

  return (
    <SiteLayout>
      <title>关于本站 | 深夜爆料的定位、内容原则与核验标准</title>
      <meta
        name="description"
        content="深夜爆料是一个接收匿名线索并做分级整理的编辑型站点。本页说明我们的品牌定位、内容原则、核验标准、发展历程与联系方式。"
      />
      <meta name="keywords" content="深夜爆料,深夜爆料编辑室,匿名投稿,内容原则,核验标准" />
      <link rel="canonical" href={`${SITE}/about`} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="关于本站 | 深夜爆料的定位、内容原则与核验标准" />
      <meta property="og:description" content="说明我们的品牌定位、内容原则、核验标准、发展历程与联系方式。" />
      <meta property="og:url" content={`${SITE}/about`} />
      <meta name="twitter:card" content="summary_large_image" />
      <JsonLd data={aboutJsonLd} />

      <div className="pt-16">
        <section className="ink-panel relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=quiet%20late%20night%20editing%20room%20with%20a%20single%20desk%20lamp%2C%20stacked%20papers%20and%20a%20closed%20laptop%2C%20deep%20shadows%2C%20warm%20amber%20light%2C%20moody%20cinematic%20editorial%20photography%2C%20no%20people&width=1600&height=800&seq=about-night-room-2026&orientation=landscape"
              alt="深夜编辑室中亮着的一盏台灯，象征深夜爆料的夜班值守"
              title="关于深夜爆料 - 夜班编辑室"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/85 to-foreground-950/60" />
          <div className="wrap relative py-16 md:py-24">
            <nav aria-label="面包屑" className="flex items-center gap-2 font-label text-xs text-background-400">
              <Link to="/" className="transition-colors hover:text-accent-300">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-200">关于本站</span>
            </nav>
            <h1 className="mt-6 max-w-3xl font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
              我们只在夜里工作，
              <br className="hidden md:block" />
              因为线索总是收工之后才来
            </h1>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              深夜爆料不追热点、不预测走向，也不替任何人下结论。我们做的是一件很朴素的事：
              给愿意开口的人一个安全的入口，把收到的说法整理成可以被检验的条目。
            </p>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="wrap py-14 md:py-20">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <SectionHeading
                  index="01"
                  eyebrow="Who We Are"
                  title="一个只负责整理，不负责审判的编辑室"
                />
              </div>
              <div className="lg:col-span-7">
                <div className="space-y-5 text-[15px] leading-[1.9] text-foreground-800">
                  <p>
                    我们的成员来自影视、综艺与演出的一线岗位。正因为曾在现场待过，所以很清楚一件事：
                    大多数从业者不是不想说，而是不知道该跟谁说、说了会不会有后果。
                  </p>
                  <p>
                    于是我们把这套流程做成了固定的东西——一个不需要署名的入口，一套统一的脱敏规则，
                    一份会持续更新的等级标准。投稿人只需要描述事实，剩下的整理、印证与呈现交给编辑室。
                  </p>
                  <p>
                    本站的内容不构成任何事实认定，所有条目都以「线索」为单位呈现。我们更希望它被当作一份
                    可检索的行业观察档案，而不是一份判决书。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100">
          <div className="wrap py-14 md:py-20">
            <SectionHeading
              index="02"
              eyebrow="Principles"
              title="四条内容原则"
              desc="它们决定了我们收什么、怎么写，以及什么时候必须停下。"
              align="center"
            />
            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-5 rounded-lg border border-background-200 bg-background-50 p-6"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <span>
                    <span className="block font-heading text-lg font-bold text-foreground-950">
                      {item.title}
                    </span>
                    <span className="mt-2 block text-sm leading-relaxed text-foreground-600">
                      {item.desc}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-50">
          <div className="wrap py-14 md:py-20">
            <SectionHeading index="03" eyebrow="Milestones" title="从一封邮件到线索库" />
            <ol className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 md:grid-cols-4">
              {milestones.map((item) => (
                <li key={item.year} className="bg-background-50 p-6">
                  <span className="font-heading text-2xl font-black text-accent-600">
                    {item.year}
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="bg-background-100">
          <div className="wrap py-14 md:py-20">
            <SectionHeading
              index="04"
              eyebrow="Voices"
              title="来自线人与读者的反馈"
              desc="以下内容经投稿人同意后脱敏发布，仅保留身份大类。"
            />
            <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
              {voices.map((voice) => (
                <figure
                  key={voice.role}
                  className="flex h-full flex-col justify-between rounded-lg border border-background-200 bg-background-50 p-6"
                >
                  <span className="font-heading text-3xl leading-none text-primary-400">“</span>
                  <blockquote className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">
                    {voice.quote}
                  </blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 border-t border-background-200 pt-4 font-label text-xs text-foreground-500">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                      <i className="ri-user-3-line" />
                    </span>
                    {voice.role}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="wrap py-14 md:py-20">
            <div className="grid grid-cols-1 gap-10 rounded-lg border border-background-200 bg-background-100 p-6 md:p-10 lg:grid-cols-12">
              <div className="lg:col-span-6">
                <SectionHeading
                  index="05"
                  eyebrow="Contact"
                  title="联系夜班编辑室"
                  desc="投稿、更正请求与内容合作都可以从这里开始。更正请求请附上可核对的信息来源。"
                />
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/#submit"
                    className="btn-base bg-primary-500 px-6 py-3 text-background-50 hover:bg-primary-600"
                  >
                    <i className="ri-mic-line text-base" />
                    匿名投递线索
                  </Link>
                  <Link
                    to="/leaks"
                    className="btn-base border border-background-300 px-6 py-3 text-foreground-800 hover:border-primary-400 hover:text-primary-600"
                  >
                    浏览线索库
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-6">
                <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 sm:grid-cols-2">
                  <div className="bg-background-50 p-5">
                    <dt className="font-label text-xs text-foreground-500">投稿邮箱</dt>
                    <dd className="mt-2 text-sm text-foreground-900">
                      tips@midnight-leaks.example.com
                    </dd>
                  </div>
                  <div className="bg-background-50 p-5">
                    <dt className="font-label text-xs text-foreground-500">值守时间</dt>
                    <dd className="mt-2 text-sm text-foreground-900">每日 00:00 — 06:00</dd>
                  </div>
                  <div className="bg-background-50 p-5">
                    <dt className="font-label text-xs text-foreground-500">更正处理</dt>
                    <dd className="mt-2 text-sm text-foreground-900">收到可核对证据后 48 小时内回复</dd>
                  </div>
                  <div className="bg-background-50 p-5">
                    <dt className="font-label text-xs text-foreground-500">来源保护</dt>
                    <dd className="mt-2 text-sm text-foreground-900">默认脱敏，不记录身份信息</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}